import React from 'react';
import { IonButton, IonLoading } from '@ionic/react';
import * as Yup from 'yup';
import 'yup-phone-lite';

import styles from './RegistrationForm.module.css';
import {
  Formik,
  Form,
  FormikHelpers,
  FormikProps,
  Field,
  ErrorMessage,
} from 'formik';
import {
  AppError,
  registerUser,
  Registration,
  saveUserCredentials,
  userLogin,
} from '../lib/functionality';

interface RegistrationFormValues {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  password: string;
  confirm_password: string;
}

const defaultFormValues: RegistrationFormValues = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  password: '',
  confirm_password: '',
};

const RegistrationFormSchema = Yup.object().shape({
  firstname: Yup.string().required('First name required'),
  lastname: Yup.string().required('Last name required'),
  email: Yup.string().email().required('Email address required'),
  phone: Yup.string().phone().required('Mobile number required'),
  password: Yup.string().min(6).required(),
  confirm_password: Yup.string().oneOf(
    [Yup.ref('password')],
    'Passwords must match',
  ),
});

interface RegistrationFormProps {
  cancelRegistration: () => void;
  successfulRegistration: () => void;
}

const RegistrationForm = ({
  cancelRegistration,
  successfulRegistration,
}: RegistrationFormProps) => {
  const onFormSubmit = async (
    values: RegistrationFormValues,
    { setSubmitting, setFieldError }: FormikHelpers<RegistrationFormValues>,
  ) => {
    const newRegistration: Registration = {
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      mobilenumber: values.phone,
      password: values.password,
    };

    try {
      await registerUser(newRegistration);

      //use submitted credentials to log in
      const userToken = await userLogin(values.email, values.password);
      await saveUserCredentials(values.email, values.password, userToken);

      successfulRegistration();
    } catch (err) {
      setFieldError('email', (err as AppError).message);
    }
  };

  return (
    <div className={styles.component}>
      <Formik
        onSubmit={onFormSubmit}
        initialValues={defaultFormValues}
        validationSchema={RegistrationFormSchema}
        validateOnBlur={true}>
        {(props: FormikProps<RegistrationFormValues>) => {
          const { isSubmitting } = props;
          return (
            <Form className={styles.registrationForm}>
              <IonLoading
                showBackdrop={true}
                animated={true}
                isOpen={isSubmitting}
                message="Submitting registration..."
              />
              <div className={styles.formRow}>
                <Field
                  id="firstname"
                  name="firstname"
                  type="text"
                  disabled={isSubmitting}
                />
                <label htmlFor="firstname">First Name</label>
                <ErrorMessage name="firstname">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRow}>
                <Field
                  id="lastname"
                  name="lastname"
                  type="text"
                  disabled={isSubmitting}
                />
                <label htmlFor="lastname">Last Name</label>
                <ErrorMessage name="lastname">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRow}>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  disabled={isSubmitting}
                />
                <label htmlFor="email">Email Address</label>
                <ErrorMessage name="email">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRow}>
                <Field
                  id="phone"
                  name="phone"
                  type="tel"
                  disabled={isSubmitting}
                />
                <label htmlFor="phone">Mobile Number</label>
                <ErrorMessage name="phone">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRow}>
                <Field
                  id="password"
                  name="password"
                  type="password"
                  disabled={isSubmitting}
                />
                <label htmlFor="password">Password</label>
                <ErrorMessage name="password">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRow}>
                <Field
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  disabled={isSubmitting}
                />
                <label htmlFor="confirm_password">Confirm Password</label>
                <ErrorMessage name="confirm_password">
                  {msg => <span className={styles.error}>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className={styles.formRowButtons}>
                <IonButton type="submit" expand="block">
                  Register
                </IonButton>
                <IonButton
                  onClick={cancelRegistration}
                  color="tertiary"
                  expand="block">
                  Cancel
                </IonButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default RegistrationForm;
